import React from 'react'
import { css } from '@emotion/react'
import Link from 'next/link'
import { colors, font } from 'plume-ui'
import { Order, Sort } from '@/lib/api/round'
import { formatWithComma } from '@/lib/number'
import { COMPANY_HREF, INVESTOR_HREF } from '@/lib/url'
import { Round } from '../../../types'
import { TagData } from '../../../types/api'
import { A } from '../../atoms/A'
import { TagButton, TagButtonGroup } from '../../atoms/TagButton'
import { OneThousandYen } from './molecules/OneThousandYen'
import { ToggleSortButton } from './molecules/ToggleSortButton'
import { IGNORED_BY_WOVN_CLASS_NAME } from '@/lib/wovn'
import { FormattedDate } from '../../atoms/FormattedDate'
import { Help } from '@/components/common/Help'

export const SeriesViewResultTable: React.FC<{
  rounds: Round[]
  order: Order
  sort: Sort
  toggleSort: (selectedSort: Sort) => void
}> = (props) => {
  return (
    <table
      css={css`
        background-color: ${colors.white00};
        border-top: 1px solid ${colors.gray60};
        border-collapse: collapse;
        display: block;
        min-height: 200px;
        overflow-y: visible;
        overflow-x: scroll;
        white-space: nowrap;
        margin-bottom: 50px;
      `}
    >
      <thead
        css={css`
          tr {
            th {
              color: ${colors.textStrong};
              background-color: ${colors.gray40};
              border-right: 1px solid ${colors.white00};
              display: table-cell;
              height: 40px;
              padding: 2px 14px;
              position: relative;
              font-weight: ${font.weight.bold};
              font-size: 12px;
              text-align: left;
            }
          }
        `}
      >
        <tr>
          <th>
            期間
            <ToggleSortButton
              sort={props.sort}
              order={props.order}
              toggleSort={props.toggleSort}
              sortType={'endDate'}
            />
          </th>
          <th
            css={css`
              min-width: 430px;
            `}
          >
            スタートアップ
          </th>
          <th>
            スピーダ調達シリーズ
            <Help linkTo={process.env.INITIAL_SERIES_HELP_ENDPOINT} />
          </th>
          <th>
            調達額
            <OneThousandYen />
            {/* <ToggleSortButton
              sort={props.sort}
              order={props.order}
              toggleSort={props.toggleSort}
              sortType={'amount'}
            /> */}
          </th>
          <th>
            調達前評価額
            <OneThousandYen />
            <ToggleSortButton
              sort={props.sort}
              order={props.order}
              toggleSort={props.toggleSort}
              sortType={'preValue'}
            />
          </th>
          <th>
            調達後評価額
            <OneThousandYen />
            <ToggleSortButton
              sort={props.sort}
              order={props.order}
              toggleSort={props.toggleSort}
              sortType={'postValue'}
            />
          </th>
          <th>投資家</th>
          <th>該当ラウンド行</th>
          <th>更新日</th>
        </tr>
      </thead>
      <tbody
        css={css`
          tr:hover {
            background: ${colors.white10};
          }
          tr {
            border-bottom: 1px solid ${colors.gray60};
            td {
              font-size: 14px;
              padding: 8px 14px;
              display: table-cell;
              position: relative;
            }
          }
        `}
      >
        {props.rounds.map((round, i) => (
          <tr key={i}>
            <td className={IGNORED_BY_WOVN_CLASS_NAME}>
              <FormattedDate>{round.startDate}</FormattedDate>
              {` `}〜{` `}
              <FormattedDate>{round.endDate}</FormattedDate>
            </td>
            <td>
              <Link href={COMPANY_HREF} as={round.companyPath} passHref>
                <A
                  css={css`
                    font-size: 14px;
                  `}
                  decoration="none"
                  data-test-id="company-name"
                >
                  {round.companyName}
                </A>
              </Link>
              <Tags tags={round.tags} />
            </td>
            <td>{round.initialSeries}</td>
            <td
              css={css`
                text-align: right;
              `}
              className={IGNORED_BY_WOVN_CLASS_NAME}
            >
              {formatWithComma(round.amount)}
            </td>
            <td
              css={css`
                text-align: right;
              `}
              className={IGNORED_BY_WOVN_CLASS_NAME}
            >
              {formatWithComma(round.preValue)}
            </td>
            <td
              css={css`
                text-align: right;
              `}
              className={IGNORED_BY_WOVN_CLASS_NAME}
            >
              {formatWithComma(round.postValue)}
            </td>
            <td>
              {round.investors.map((investor, j) => {
                return (
                  <Link
                    href={INVESTOR_HREF}
                    as={investor.path}
                    passHref
                    key={j}
                  >
                    <A
                      css={css`
                        margin: 4px 0;
                        display: block;
                      `}
                      decoration="none"
                      data-test-id="investor-name"
                    >
                      {investor.name}
                    </A>
                  </Link>
                )
              })}
            </td>
            <td className={IGNORED_BY_WOVN_CLASS_NAME}>
              {round.fundingRoundPathList.map((fundingRound, j) => {
                return (
                  <A
                    href={fundingRound.path}
                    key={j}
                    css={css`
                      margin: 4px 0;
                      display: block;
                    `}
                    decoration="none"
                  >
                    {fundingRound.shortname}
                  </A>
                )
              })}
            </td>
            <td className={IGNORED_BY_WOVN_CLASS_NAME}>
              <FormattedDate>{round.updatedAt}</FormattedDate>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const Tags: React.FC<{
  tags: TagData[]
}> = (props) => {
  return (
    <TagButtonGroup
      css={css`
        margin-top: 8px;
        white-space: normal;
      `}
    >
      {props.tags.map((tag, i) => (
        <TagButton key={i} tag={tag} />
      ))}
    </TagButtonGroup>
  )
}
